import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { initialPagination } from '@components/shared/Table/components/Pagination/types'
import { PageWrapper } from '@components/shared/PageWrapper'
import { Title } from '@components/shared/Title'
import { toast } from '@utils/functions/notificationToast'
import { toReferenceDateIso } from '@utils/dates/toReferenceDateIso'
import { downloadBlobFile } from '@utils/functions/downloadBlobFile'
import { usePreview } from '@pages/Preview/context'
import { useUser } from '@hooks/useUser'

import { ApprovalFlowRow } from './components/ApprovalFlowRow'
import { ApprovalWorkflowBoard } from './components/ApprovalWorkflowBoard'
// import { ConsolidatedSummaryFilters } from './components/ConsolidatedSummaryFilters'
import { ConsolidatedSummaryTable } from './components/ConsolidatedSummaryTable'
import { ExportTitle } from './components/ExportTitle'
import { generateExcelFileName } from './helpers/generateExcelFileName'
import { PreviewHistoryCard } from './components/PreviewHistoryCard'
import { PreviewManagementFilters } from './components/PreviewManagementFilters'
import { UnitClassificationTable } from './components/UnitClassificationTable'
import { UnitsListModal } from './components/UnitsListModal'
import { WeekStatusCards } from './components/WeekStatusCards'
import { WeekStatusRelationModal } from './components/WeekStatusCards/WeekStatusRelationModal'
import { useEmptySearchResultsToast } from './hooks/useEmptySearchResultsToast'

import type { SortProps } from '@components/shared/Table/types'
import type { ApprovalWorkflowStatusClickProps } from './components/ApprovalWorkflowBoard/types'
import type { ConsolidatedSummaryFiltersFormProps } from './components/ConsolidatedSummaryFilters/form/types'
import type { PreviewManagementFiltersFormProps } from './components/PreviewManagementFilters/form/types'
import type { WeekRelationModalData } from './components/WeekStatusCards/WeekStatusRelationModal/types'

import * as apiPreviewManagement from '@services/api/previewManagement'

type UnitsListModalState = ApprovalWorkflowStatusClickProps & { units: string[] }

export const PreviewManagement = () => {
  const { periodSelectedData } = usePreview()
  const { filiData } = useUser()

  const [unitsListModal, setUnitsListModal] = useState<UnitsListModalState | null>(null)
  const [weekRelationModal, setWeekRelationModal] = useState<WeekRelationModalData | null>(null)
  const [activeWeekNumber, setActiveWeekNumber] = useState<number | null>(null)
  const [consolidatedSummaryFilters] = useState<ConsolidatedSummaryFiltersFormProps | null>(null)
  const [previewManagementFilters, setPreviewManagementFilters] = useState<PreviewManagementFiltersFormProps | null>(null)
  const [unitClassificationSort, setUnitClassificationSort] = useState<SortProps | null>(null)
  const [unitClassificationParams, setUnitClassificationParams] = useState<{ currentPage: number; itemsPerPage: number }>({
    currentPage: initialPagination.PAGE,
    itemsPerPage: initialPagination.ITEMS_PER_PAGE
  })
  const [isExportingSummary, setIsExportingSummary] = useState(false)
  const [isExportingClassification, setIsExportingClassification] = useState(false)

  const referenceDate = periodSelectedData?.competenceDate

  const toArrayFilter = (value?: string | null) => (value && value !== 'all' ? [value] : undefined)

  const toMultiFilter = (values?: string[] | null) => {
    const filtered = values?.filter((value) => value !== 'all')
    return filtered && filtered.length > 0 ? filtered : undefined
  }

  const previewFiltersPayload = {
    branchCodeFilter: filiData?.filiCode ? [filiData.filiCode] : undefined,
    businessUnitFilter: toArrayFilter(previewManagementFilters?.businessUnit),
    boardOfDirectorsFilter: toMultiFilter(previewManagementFilters?.directorate),
    portfolioFilter: toMultiFilter(previewManagementFilters?.portfolio),
    economicGroupFilter: toMultiFilter(previewManagementFilters?.economicGroup),
    productFilter: toMultiFilter(previewManagementFilters?.segment),
    serviceTypeFilter: toMultiFilter(previewManagementFilters?.serviceType),
    goNamesFilter: toMultiFilter(previewManagementFilters?.goName),
    gceNamesFilter: toMultiFilter(previewManagementFilters?.gceName)
  }

  const consolidatedSummaryPayload = {
    referenceDate: referenceDate!,
    startDate: toReferenceDateIso(consolidatedSummaryFilters?.startDate || '') || undefined,
    endDate: toReferenceDateIso(consolidatedSummaryFilters?.endDate || '') || undefined,
    ...previewFiltersPayload
  }

  const branchClassificationPayload = {
    referenceDate: referenceDate!,
    sortBy: unitClassificationSort?.sortBy,
    sortType: unitClassificationSort?.sortType,
    currentPage: unitClassificationParams.currentPage,
    itemsPerPage: unitClassificationParams.itemsPerPage,
    ...previewFiltersPayload
  }

  const searchSignature = JSON.stringify({
    referenceDate,
    previewFiltersPayload,
    consolidatedSummaryFilters,
    unitClassificationSort,
    unitClassificationParams
  })

  const { reportSearchResult } = useEmptySearchResultsToast(searchSignature)

  const {
    data: consolidatedSummaryData,
    isLoading: consolidatedSummaryLoading,
    isSuccess: consolidatedSummaryIsSuccess
  } = useQuery({
    queryKey: ['consolidated-financial-summary', referenceDate, consolidatedSummaryFilters, previewFiltersPayload],
    queryFn: () => apiPreviewManagement.getConsolidatedFinancialSummary(consolidatedSummaryPayload),
    enabled: !!referenceDate
  })

  useEffect(() => {
    if (!consolidatedSummaryIsSuccess) return
    reportSearchResult('consolidatedSummary', !!consolidatedSummaryData)
  }, [consolidatedSummaryIsSuccess, consolidatedSummaryData])

  const {
    data: unitClassificationData,
    isLoading: unitClassificationLoading,
    isSuccess: unitClassificationIsSuccess
  } = useQuery({
    queryKey: ['branch-classification', referenceDate, unitClassificationSort, unitClassificationParams, previewFiltersPayload],
    queryFn: () => apiPreviewManagement.getBranchClassification(branchClassificationPayload),
    enabled: !!referenceDate
  })

  useEffect(() => {
    if (!unitClassificationIsSuccess) return
    reportSearchResult('unitClassification', !!unitClassificationData)
  }, [unitClassificationIsSuccess, unitClassificationData])

  const handleExportConsolidatedSummary = async () => {
    if (!referenceDate) return

    setIsExportingSummary(true)

    try {
      const blob = await apiPreviewManagement.exportConsolidatedFinancialSummary(consolidatedSummaryPayload)
      downloadBlobFile(blob, generateExcelFileName('ResumoFinanceiroConsolidado'))
      toast.success('Exportação do Resumo Financeiro Consolidado realizada com sucesso!')
    } catch (error) {
      toast.error('Não foi possível exportar o Resumo Financeiro Consolidado. Tente novamente.')
    } finally {
      setIsExportingSummary(false)
    }
  }

  const handleExportBranchClassification = async () => {
    if (!referenceDate) return

    setIsExportingClassification(true)

    try {
      const blob = await apiPreviewManagement.exportBranchClassification(branchClassificationPayload)
      downloadBlobFile(blob, generateExcelFileName('ClassificacaoUnidades'))
      toast.success('Exportação da Classificação das Unidades realizada com sucesso!')
    } catch (error) {
      toast.error('Não foi possível exportar a Classificação das Unidades. Tente novamente.')
    } finally {
      setIsExportingClassification(false)
    }
  }

  return (
    <PageWrapper
      breadcrumbData={[{ label: 'Gestão Financeira' }, { label: 'Prévia' }, { label: 'Gestão da Prévia' }]}
    >
      <PreviewManagementFilters onFilter={setPreviewManagementFilters} />

      <WeekStatusCards
        referenceDate={periodSelectedData?.competenceDate}
        filters={previewFiltersPayload}
        onOpenWeekRelation={setWeekRelationModal}
        onActiveWeekChange={setActiveWeekNumber}
        onSearchResult={(hasData) => reportSearchResult('weeklyStatus', hasData)}
      />

      <PreviewHistoryCard filters={previewManagementFilters} />

      <Title label="Fluxo de Aprovação (das Unidades)" />

      <ApprovalFlowRow
        referenceDate={referenceDate}
        weekNumber={activeWeekNumber}
        filters={previewFiltersPayload}
        onOpenUnitsList={setUnitsListModal}
      />

      <ApprovalWorkflowBoard
        referenceDate={referenceDate}
        weekNumber={activeWeekNumber}
        filters={previewFiltersPayload}
        onOpenUnitsList={setUnitsListModal}
        onSearchResult={(hasData) => reportSearchResult('hierarchy', hasData)}
      />

      <ExportTitle
        label="Resumo Financeiro Consolidado"
        onExport={handleExportConsolidatedSummary}
        isExporting={isExportingSummary}
      />

      {/* <ConsolidatedSummaryFilters onFilter={setConsolidatedSummaryFilters} /> */}

      <ConsolidatedSummaryTable rows={consolidatedSummaryData?.rows} isLoading={consolidatedSummaryLoading} />

      <ExportTitle
        label="Classificação das Unidades"
        onExport={handleExportBranchClassification}
        isExporting={isExportingClassification}
      />

      <UnitClassificationTable
        rows={unitClassificationData?.rows}
        isLoading={unitClassificationLoading}
        onSort={setUnitClassificationSort}
        pagination={{
          currentPage: unitClassificationData?.currentPage ?? unitClassificationParams.currentPage,
          totalPages: unitClassificationData?.totalPages,
          itemsPerPage: unitClassificationData?.itemsPerPage ?? unitClassificationParams.itemsPerPage,
          totalItems: unitClassificationData?.totalItems,
          onPageChange: (currentPage) => setUnitClassificationParams((prev) => ({ ...prev, currentPage })),
          onItemsPerPageChange: (itemsPerPage) =>
            setUnitClassificationParams({ currentPage: initialPagination.PAGE, itemsPerPage })
        }}
      />

      <UnitsListModal
        open={!!unitsListModal}
        onClose={() => setUnitsListModal(null)}
        statusIcon={unitsListModal?.icon}
        statusLabel={unitsListModal?.label ?? ''}
        statusColor={unitsListModal?.color ?? ''}
        statusBackground={unitsListModal?.background ?? ''}
        listTitle={`CLs ${unitsListModal ? unitsListModal.label.charAt(0).toLowerCase() + unitsListModal.label.slice(1) : ''}`}
        units={unitsListModal?.units ?? []}
      />

      <WeekStatusRelationModal
        open={!!weekRelationModal}
        onClose={() => setWeekRelationModal(null)}
        data={weekRelationModal}
      />
    </PageWrapper>
  )
}
